// Het wisselen van de tabbladen in het uitlegblok.
//
// Wat dit script wel doet: van tabblad wisselen, vanzelf doorbladeren, en
// pauzeren. Wat het niet doet: de inhoud opbouwen. Alle vier de panelen staan al
// in de pagina. Hapert dit script, dan zie je het eerste paneel en is er niets
// kapot.
//
// Het doorbladeren luistert naar de animatie van de voortgangsstreep in plaats
// van naar een eigen teller. Zo lopen streep en wissel altijd gelijk, ook als de
// pagina het even druk heeft of als je van tabblad naar tabblad springt.

class UitlegBlok extends HTMLElement {
  connectedCallback() {
    if (this.ingesteld) return;
    this.ingesteld = true;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panelen = [...this.querySelectorAll('[role="tabpanel"]')];
    this.pauzeKnop = this.querySelector(".uitleg__pauze");
    if (this.tabs.length < 2) return;

    this.huidig = 0;
    this.gepauzeerd = true;
    this.doorEenMensGepauzeerd = true;
    this.pauzeer(true);

    this.tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => this.naar(i, { doorEenMens: true }));
      tab.addEventListener("keydown", (e) => this.toets(e, i));
    });

    // De wissel hangt aan het einde van de animatie van de streep. Die animatie
    // staat op het actieve tabblad, dus we luisteren op het hele blok.
    this.addEventListener("animationend", (e) => {
      if (e.animationName !== "uitleg-vullen" || this.gepauzeerd) return;
      this.naar((this.huidig + 1) % this.tabs.length);
    });

    this.pauzeKnop?.addEventListener("click", () => {
      this.doorEenMensGepauzeerd = !this.gepauzeerd;
      this.pauzeer(!this.gepauzeerd);
    });

    // Wie minder beweging heeft ingesteld, krijgt geen doorbladeren. De streep
    // staat dan meteen vol; dat regelt de CSS.
    const stil = matchMedia("(prefers-reduced-motion: reduce)");
    if (stil.matches) this.pauzeer(true);

    // Buiten beeld hoeft het niet door te lopen. Dat scheelt rekenwerk op een
    // telefoon en het is minder verwarrend: je mist geen tabbladen die langs
    // zijn gekomen terwijl je ergens anders keek.
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        ([item]) => {
          if (this.doorEenMensGepauzeerd) return;
          this.pauzeer(!item.isIntersecting, { stil: true });
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);
    }
  }

  naar(i, { doorEenMens = false } = {}) {
    if (doorEenMens) {
      this.doorEenMensGepauzeerd = true;
      this.pauzeer(true);
    }
    if (i === this.huidig) return;

    this.tabs[this.huidig].classList.remove("uitleg__tab--aan");
    this.tabs[this.huidig].setAttribute("aria-selected", "false");
    this.tabs[this.huidig].tabIndex = -1;
    this.panelen[this.huidig].hidden = true;
    this.panelen[this.huidig].classList.remove("uitleg__paneel--aan");

    this.huidig = i;

    this.tabs[i].classList.add("uitleg__tab--aan");
    this.tabs[i].setAttribute("aria-selected", "true");
    this.tabs[i].tabIndex = 0;
    this.panelen[i].hidden = false;
    this.panelen[i].classList.add("uitleg__paneel--aan");

    // Op een smal scherm schuift de rij tabbladen; het actieve moet in beeld
    // blijven, anders lijkt het alsof er niets gebeurt. Alleen de rij zelf
    // schuift, nooit de pagina: scrollIntoView deed dat wel en trok bij elke
    // wissel de hele pagina naar deze sectie (Bram, 8 september 2026).
    const rij = this.tabs[i].parentElement;
    if (rij && rij.scrollWidth > rij.clientWidth) {
      const tab = this.tabs[i];
      const doel = tab.offsetLeft - (rij.clientWidth - tab.offsetWidth) / 2;
      rij.scrollTo({ left: Math.max(0, doel), behavior: "smooth" });
    }

    // Klikt iemand zelf, dan stopt het doorbladeren. Vanzelf verder gaan terwijl
    // iemand net iets heeft aangewezen, is het ergerlijkste wat zo'n sectie kan
    // doen.
    if (doorEenMens) {
      this.doorEenMensGepauzeerd = true;
      this.pauzeer(true);
      this.tabs[i].focus();
    }
  }

  pauzeer(aan, { stil = false } = {}) {
    const label = this.pauzeKnop?.querySelector('.visually-hidden');
    if (label) label.textContent = aan ? 'Start automatisch doorbladeren' : 'Pauzeer het doorbladeren';
    this.gepauzeerd = aan;
    this.toggleAttribute("data-pauze", aan);
    if (aan) this.setAttribute("data-pauze", "ja");
    if (this.pauzeKnop && !stil) this.pauzeKnop.setAttribute("aria-pressed", String(aan));
    if (this.pauzeKnop && stil) this.pauzeKnop.setAttribute("aria-pressed", String(aan));
  }

  // Pijltjes naar links en rechts lopen door de tabbladen, zoals het hoort bij
  // een tablist. Home en End springen naar het begin en het einde.
  toets(e, i) {
    const laatste = this.tabs.length - 1;
    let doel = null;
    if (e.key === "ArrowRight") doel = i === laatste ? 0 : i + 1;
    if (e.key === "ArrowLeft") doel = i === 0 ? laatste : i - 1;
    if (e.key === "Home") doel = 0;
    if (e.key === "End") doel = laatste;
    if (doel === null) return;
    e.preventDefault();
    this.naar(doel, { doorEenMens: true });
  }
}

if (!customElements.get("uitleg-blok")) {
  customElements.define("uitleg-blok", UitlegBlok);
}
